(function registerOrthoraPdpForm(root) {
  const utils = {
    bundleUnitPrice(cents, quantity) {
      if (quantity < 2) return Number(cents || 0);
      const multiplier = quantity >= 3 ? 0.8 : quantity === 2 ? 0.9 : 1;
      return Math.round((Number(cents || 0) * multiplier) / 100) * 100;
    },

    resolveVariant(variants, selectedOptions) {
      if (!Array.isArray(variants) || !Array.isArray(selectedOptions))
        return null;

      return (
        variants.find((variant) => {
          if (
            !Array.isArray(variant.options) ||
            variant.options.length !== selectedOptions.length
          )
            return false;
          return variant.options.every(
            (option, index) => option === selectedOptions[index],
          );
        }) || null
      );
    },

    resolveBundleItems(variants, selectedRows) {
      if (!Array.isArray(variants) || !Array.isArray(selectedRows)) return null;
      const items = selectedRows.map((selectedOptions) => {
        const variant = utils.resolveVariant(variants, selectedOptions);
        if (!variant?.available) return null;
        return { id: variant.id, quantity: 1 };
      });
      return items.every(Boolean) ? items : null;
    },

    formatMoney(cents, format) {
      const token = String(format || "${{amount}}");
      const formats = {
        amount: [2, ",", "."],
        amount_no_decimals: [0, ",", "."],
        amount_with_comma_separator: [2, ".", ","],
        amount_no_decimals_with_comma_separator: [0, ".", ","],
        amount_with_apostrophe_separator: [2, "'", "."],
        amount_no_decimals_with_space_separator: [0, " ", "."],
      };

      return token.replace(/{{\s*(\w+)\s*}}/g, (_match, placeholder) => {
        const [precision, thousands, decimal] =
          formats[placeholder] || formats.amount;
        const fixed = (Number(cents || 0) / 100).toFixed(precision);
        const [whole, fraction] = fixed.split(".");
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction === undefined
          ? grouped
          : `${grouped}${decimal}${fraction}`;
      });
    },
  };

  root.OrthoraPdpFormUtils = utils;

  if (
    typeof customElements === "undefined" ||
    typeof HTMLElement === "undefined"
  )
    return;
  if (customElements.get("orthora-pdp-form")) return;

  customElements.define(
    "orthora-pdp-form",
    class OrthoraPdpForm extends HTMLElement {
      connectedCallback() {
        if (this.formInitialized) return;
        this.formInitialized = true;

        this.optionInputs = Array.from(
          this.querySelectorAll("[data-product-option]"),
        );
        this.variantInput = this.querySelector("[data-variant-id]");
        this.quantityInput = this.querySelector("[data-product-quantity]");
        this.availability = this.querySelector("[data-product-availability]");
        this.addButton = this.querySelector("[data-add-to-cart]");
        this.addButtonText = this.querySelector("[data-add-to-cart-text]");
        this.addText = this.dataset.addText || "ADD TO CART →";
        this.soldOutText = this.dataset.soldOutText || "SOLD OUT";
        this.unavailableText =
          this.dataset.unavailableText || "UNAVAILABLE OPTION";
        this.bundleCards = Array.from(
          this.querySelectorAll("[data-bundle-quantity]"),
        );
        this.bundleOptionInputs = Array.from(
          this.querySelectorAll("[data-bundle-option]"),
        );
        this.cartLines = this.querySelector("[data-bundle-cart-lines]");
        this.variants = this.readVariants();
        this.chartOverlay = this.querySelector("[data-size-chart-overlay]");
        this.chartTrigger = this.querySelector("[data-size-chart-open]");
        this.chartClose = this.querySelector("[data-size-chart-close]");
        this.chartKind = this.chartOverlay?.dataset.sizeChartKind || "";
        this.chartOpenedAt = 0;
        this.pageScrollState = null;
        this.boundChartKeydown = (event) => this.handleChartKeydown(event);
        this.chartDocument =
          this.ownerDocument &&
          typeof this.ownerDocument.addEventListener === "function"
            ? this.ownerDocument
            : typeof document !== "undefined"
              ? document
              : null;

        this.optionInputs.forEach((input) =>
          input.addEventListener("change", () => this.updateVariant()),
        );
        this.bundleCards.forEach((card) => {
          card
            .querySelector("input")
            ?.addEventListener("change", () => this.updateQuantity(card));
        });
        this.bundleOptionInputs.forEach((input) =>
          input.addEventListener("change", () => this.syncBundleCartLines()),
        );
        this.chartTrigger?.addEventListener("click", () =>
          this.openSizeChart(),
        );
        this.chartClose?.addEventListener("click", () =>
          this.requestSizeChartClose("close"),
        );
        this.chartOverlay?.addEventListener("click", (event) => {
          if (event.target === this.chartOverlay)
            this.requestSizeChartClose("outside");
        });
        this.chartDocument?.addEventListener(
          "keydown",
          this.boundChartKeydown,
        );

        this.updateVariant();
        const initialBundle =
          this.bundleCards.find((card) => card.querySelector("input")?.checked) ||
          this.bundleCards.find((card) => card.dataset.bundleQuantity === "1");
        if (initialBundle) this.updateQuantity(initialBundle);
      }

      disconnectedCallback() {
        this.chartDocument?.removeEventListener(
          "keydown",
          this.boundChartKeydown,
        );
        if (this.chartOverlay?.hasAttribute("data-open"))
          this.closeSizeChart();
      }

      readVariants() {
        const source = this.querySelector("[data-product-variants]");
        if (!source) return [];

        try {
          return JSON.parse(source.textContent || "[]");
        } catch (_error) {
          return [];
        }
      }

      selectedOptions() {
        return this.optionInputs.map((input) => input.value);
      }

      updateVariant() {
        const variant = utils.resolveVariant(
          this.variants,
          this.selectedOptions(),
        );
        this.renderVariant(variant);
      }

      renderVariant(variant) {
        const available = Boolean(variant?.available);
        this.currentVariant = variant;

        if (this.variantInput) this.variantInput.value = variant?.id || "";
        this.dataset.selectedVariantId = variant?.id || "";
        if (this.availability) {
          this.availability.textContent = available
            ? "Available to order."
            : "Unavailable.";
          this.availability.classList.toggle("is-unavailable", !available);
        }
        if (this.addButton) this.addButton.disabled = !available;
        if (this.addButtonText)
          this.addButtonText.textContent = available
            ? this.addText
            : this.soldOutText;

        const moneyFormat = this.dataset.moneyFormat;
        const productRoot = this.closest(".orthora-pdp");
        const gallery = productRoot?.querySelector("orthora-pdp-gallery");
        gallery?.selectMedia?.(
          variant?.featured_media?.id ?? null,
          variant?.options ?? this.selectedOptions(),
        );
        const priceOutput = productRoot?.querySelector("[data-product-price]");
        const compareOutput = productRoot?.querySelector(
          "[data-product-compare-price]",
        );
        const savingsOutput = productRoot?.querySelector(
          "[data-product-savings]",
        );
        const bundlePriceOutputs = this.querySelectorAll(
          "[data-bundle-unit-price]",
        );

        if (!variant) {
          if (priceOutput) {
            priceOutput.textContent = "";
            priceOutput.hidden = true;
          }
          if (compareOutput) {
            compareOutput.textContent = "";
            compareOutput.hidden = true;
          }
          if (savingsOutput) {
            savingsOutput.textContent = "";
            savingsOutput.hidden = true;
          }
          bundlePriceOutputs.forEach((output) => {
            output.textContent = "";
            output.hidden = true;
          });
          return;
        }

        const price = utils.formatMoney(variant.price, moneyFormat);
        const compareAtPrice = Number(variant.compare_at_price || 0);

        if (priceOutput) {
          priceOutput.textContent = price;
          priceOutput.hidden = false;
        }
        bundlePriceOutputs.forEach((output, index) => {
          const quantity = Number(
            this.bundleCards[index]?.dataset.bundleQuantity || 1,
          );
          output.textContent = utils.formatMoney(
            utils.bundleUnitPrice(variant.price, quantity),
            moneyFormat,
          );
          output.hidden = false;
        });

        const hasSavings = compareAtPrice > Number(variant.price);
        if (compareOutput) {
          compareOutput.textContent = hasSavings
            ? utils.formatMoney(compareAtPrice, moneyFormat)
            : "";
          compareOutput.hidden = !hasSavings;
        }
        if (savingsOutput) {
          const percentage = hasSavings
            ? Math.round(
                ((compareAtPrice - variant.price) * 100) / compareAtPrice,
              )
            : 0;
          savingsOutput.textContent = hasSavings
            ? `YOU SAVE ${percentage}%`
            : "";
          savingsOutput.hidden = !hasSavings;
        }
        this.syncBundleCartLines();
      }

      updateQuantity(card) {
        const quantity = Number(card.dataset.bundleQuantity);
        if (!Number.isInteger(quantity) || quantity < 1) return;
        this.activeBundleCard = card;
        if (this.quantityInput) {
          this.quantityInput.value = quantity === 1 ? "1" : String(quantity);
          this.quantityInput.disabled = quantity > 1;
        }
        this.bundleCards.forEach((bundleCard) =>
          bundleCard.classList.toggle("is-selected", bundleCard === card),
        );
        this.bundleCards.forEach((bundleCard) => {
          const variants = bundleCard.querySelector?.("[data-bundle-variants]");
          if (variants) variants.hidden = bundleCard !== card;
        });
        if (quantity > 1) this.seedFirstBundleRow(card);
        this.syncBundleCartLines();
      }

      seedFirstBundleRow(card) {
        if (typeof card.querySelectorAll !== "function") return;
        const firstRow = Array.from(
          card.querySelectorAll("[data-bundle-variant-row]"),
        )[0];
        if (!firstRow || typeof firstRow.querySelectorAll !== "function") return;
        const primaryOptions = this.selectedOptions();
        Array.from(firstRow.querySelectorAll("[data-bundle-option]")).forEach(
          (input) => {
            const position = Number(input.dataset.optionPosition || 0);
            if (position > 0 && primaryOptions[position - 1] !== undefined) {
              input.value = primaryOptions[position - 1];
            }
          },
        );
      }

      selectedBundleRows(card, quantity) {
        if (typeof card?.querySelectorAll !== "function") return [];
        return Array.from(card.querySelectorAll("[data-bundle-variant-row]"))
          .slice(0, quantity)
          .map((row) =>
            Array.from(row.querySelectorAll("[data-bundle-option]"))
              .sort(
                (left, right) =>
                  Number(left.dataset.optionPosition || 0) -
                  Number(right.dataset.optionPosition || 0),
              )
              .map((input) => input.value),
          );
      }

      syncBundleCartLines() {
        const quantity = Number(
          this.activeBundleCard?.dataset.bundleQuantity ||
            this.quantityInput?.value ||
            1,
        );
        if (quantity < 2 || !this.cartLines || !this.variantInput) {
          if (this.variantInput) this.variantInput.disabled = false;
          if (this.quantityInput) this.quantityInput.disabled = false;
          this.cartLines?.replaceChildren();
          if (this.addButton) this.addButton.disabled = !this.currentVariant?.available;
          return;
        }

        const selectedRows = this.selectedBundleRows(
          this.activeBundleCard,
          quantity,
        );
        const items = utils.resolveBundleItems(this.variants, selectedRows);
        this.variantInput.disabled = true;
        if (this.quantityInput) this.quantityInput.disabled = true;
        this.cartLines.replaceChildren();

        if (!items || items.length !== quantity) {
          if (this.addButton) this.addButton.disabled = true;
          if (this.addButtonText)
            this.addButtonText.textContent = this.unavailableText;
          return;
        }

        items.forEach((item, index) => {
          for (const [key, value] of [
            ["id", item.id],
            ["quantity", item.quantity],
          ]) {
            const input = this.ownerDocument.createElement("input");
            input.type = "hidden";
            input.name = `items[${index}][${key}]`;
            input.value = String(value);
            this.cartLines.append(input);
          }
        });
        if (this.addButton) this.addButton.disabled = false;
        if (this.addButtonText) this.addButtonText.textContent = this.addText;
      }

      openSizeChart() {
        if (!this.chartOverlay || !this.chartTrigger) return;
        this.chartOpenedAt = performance.now();
        if (this.chartKind === "relentless") this.lockPageScroll();
        this.chartOverlay.inert = false;
        this.chartOverlay.setAttribute("data-open", "");
        this.chartOverlay.setAttribute("aria-hidden", "false");
        this.chartTrigger.setAttribute("aria-expanded", "true");
      }

      closeSizeChart() {
        if (!this.chartOverlay || !this.chartTrigger) return;
        this.chartOverlay.removeAttribute("data-open");
        this.chartOverlay.setAttribute("aria-hidden", "true");
        this.chartOverlay.inert = true;
        this.chartTrigger.setAttribute("aria-expanded", "false");
        if (this.chartKind === "relentless") this.restorePageScroll();
      }

      requestSizeChartClose(source) {
        if (!this.chartOverlay?.hasAttribute("data-open")) return;
        if (this.chartKind === "campaign") {
          if (source !== "escape") this.closeSizeChart();
          return;
        }
        if (performance.now() - this.chartOpenedAt < 800) return;
        this.closeSizeChart();
      }

      handleChartKeydown(event) {
        if (event.key !== "Escape") return;
        if (!this.chartOverlay?.hasAttribute("data-open")) return;
        this.requestSizeChartClose("escape");
      }

      lockPageScroll() {
        if (this.pageScrollState) return;
        if (!this.chartDocument?.documentElement || !this.chartDocument.body)
          return;
        this.pageScrollState = [
          this.chartDocument.documentElement,
          this.chartDocument.body,
        ].map(
          (element) => ({
            element,
            priority: element.style.getPropertyPriority("overflow"),
            value: element.style.getPropertyValue("overflow"),
          }),
        );
        this.pageScrollState.forEach(({ element }) =>
          element.style.setProperty("overflow", "hidden"),
        );
      }

      restorePageScroll() {
        if (!this.pageScrollState) return;
        this.pageScrollState.forEach(({ element, priority, value }) => {
          if (value) element.style.setProperty("overflow", value, priority);
          else element.style.removeProperty("overflow");
        });
        this.pageScrollState = null;
      }
    },
  );
})(globalThis);
